import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { OntologyModule } from "../../../api/content/ontologyApi";
import { getItemVersion } from "../../../api/content/itemsApi";
import { appPaths } from "../../../shared/routing/appPaths";
import CopyReference from "../../../ui/content/CopyReference";
import { useLoad } from "../../../shared/loading/useLoad";
import SourceView from "../../../ui/content/source/SourceView";
import LoadStatus from "../../../ui/feedback/LoadStatus";

/** One module's stored canonical Turtle at its exact version (content spec D-9). */
export default function ModuleTurtle({ module }: { module: OntologyModule | null }) {
  const { t } = useTranslation();
  if (module === null) {
    return <p className="app-text-muted text-sm">{t("ontologyViewer.modules.none")}</p>;
  }
  return <LoadedModuleTurtle key={module.reference} module={module} />;
}

function LoadedModuleTurtle({ module }: { module: OntologyModule }) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => getItemVersion(module.reference, signal), [module.reference]);
  const { state, reload } = useLoad(load);
  return (
    <div className="space-y-2">
      <p className="app-text-muted flex flex-wrap items-center gap-1 text-xs">
        <Link className="app-action-link" to={appPaths.item(module.reference)}>
          {t("ontologyViewer.modules.turtleOf", { name: module.name })}
        </Link>
        <CopyReference value={module.reference} label={t("ontologyViewer.copyModule")} />
      </p>
      <LoadStatus state={state} loadingLabel={t("ontologyViewer.loading")} loadingClassName="py-10" onRetry={reload} />
      {state.status === "loaded" ? <SourceView label={t("ontologyViewer.modules.turtleOf", { name: module.name })} language="turtle" value={state.value.text} presentation="full" /> : null}
    </div>
  );
}
